/** Convert the value to an array */
export function list<T>(
  obj: T | readonly T[] | null | undefined,
): readonly T[] {
  if (obj == null) return [];
  return (Array.isArray(obj) ? obj : [obj]) as readonly T[];
}

/**
 * Add or remove a value to an array (similar to a Set)
 */
export function toggle<T>(
  array: readonly T[],
  value: T,
  include: boolean,
): T[] {
  const list = array?.filter((x) => x !== value) || [];
  if (include) {
    list.push(value);
  }
  return list;
}

/** Extract an element from a list */
export function extract<T>(
  list: readonly T[] | undefined | null,
  f: (t: T) => boolean,
): [T | undefined, readonly T[]] {
  if (list == null) return [undefined, []];
  const i = list.findIndex(f);
  if (i === -1) {
    return [undefined, list];
  }
  const needle = list[i];
  const haystack = [...list.slice(0, i), ...list.slice(i + 1)];
  return [needle, haystack];
}

/** Return the first element of a list */
export function first<T>(list: T | readonly T[] | undefined): T | undefined {
  return Array.isArray(list) ? list[0] : list;
}

/** Get the last element */
export function last<T>(list: readonly T[] | undefined): T | undefined {
  return list?.[list.length - 1];
}

/** Get the value from an array if there is only 1 entry */
export function single<T>(list: readonly T[] | undefined): T | undefined {
  return list?.length === 1 ? list[0] : undefined;
}

/** Group an array using a given classifier */
export function groupBy<T, K>(
  list: readonly T[],
  classifier: (t: T) => K,
): Map<K, T[]> {
  const result = new Map<K, T[]>();
  for (const t of list) {
    const key = classifier(t);
    const group = result.get(key);
    if (group) {
      group.push(t);
    } else {
      result.set(key, [t]);
    }
  }
  return result;
}

/**
 * Can be used as .filter(nonNull) to filter out null and undefined
 */
export function nonNull<T>(t: T | undefined | null): t is T {
  return t != null;
}

/**
 * Filter null and undefined
 *
 * Equivalent to .filter(nonNull).filter(f)
 */
export function takeIf<T>(
  f: (t: T) => unknown,
): (t: T | null | undefined) => t is T {
  return (t): t is T => t != null && !!f(t);
}

/**
 * Returns true if an array exists and is not empty
 */
export function notEmpty<T>(
  array: readonly T[] | null | undefined,
): array is T[] {
  return !isNullOrEmpty(array);
}

/**
 * Returns true if an array is empty or null
 */
export function isNullOrEmpty<T>(
  array: readonly T[] | null | undefined,
): array is null | undefined {
  return array == null || array.length === 0;
}

/** Returns an empty array if it does not exist */
export function nullToEmpty<T>(
  array: readonly T[] | null | undefined,
): readonly T[] {
  return array ?? [];
}

/** Filters null and undefined from an optional list */
export function filterNull<T>(
  array: ReadonlyArray<T | null | undefined> | null | undefined,
  filter?: (t: T) => unknown,
): T[] {
  return array?.filter(filter ? takeIf(filter) : nonNull) ?? [];
}

/**
 * return true if array has data to display.
 * invalid data include null, undefined, empty strings, empty objects
 */
export function hasDataToDisplay<T>(
  array: readonly T[] | null | undefined,
): array is T[] {
  return (
    notEmpty(array) &&
    array.some((data) => {
      if (!data) return false;
      if (typeof data === 'object') return Object.keys(data).length > 0;
      return true;
    })
  );
}

/**
 * Returns an array that contains the intersection of all input arrays.
 *
 * @template T
 * @param {...T[][]} arrays - The arrays to be intersected.
 * @returns {T[]} - A new array with the common elements found in all input arrays.
 *
 * @example
 * const result = intersection([1, 2, 3], [2, 3, 4], [3, 4, 5]);
 * console.log(result); // Output: [3]
 */
export const intersection = <T>(...arrays: T[][]): T[] => {
  return arrays.reduce((a, b) => {
    return a.filter((value) => b.includes(value));
  });
};

export const sortArrayOfObjectsByPredefinedOrder = <T>({
  array,
  key,
  order,
}: {
  array: readonly T[];
  key: keyof T;
  order: readonly string[];
}): T[] => {
  return array.slice().sort((a, b) => {
    const aValue = a[key];
    const bValue = b[key];
    const aIndex = order.indexOf(aValue as string);
    const bIndex = order.indexOf(bValue as string);

    // If aValue is not in order, push it to the back
    if (aIndex === -1) return 1;
    // If bValue is not in order, push it to the back
    if (bIndex === -1) return -1;

    // Sort based on the index in the order array
    return aIndex - bIndex;
  });
};
